/**
 * Resolve a `?position=` value against a catalog item's `properties.variants`: a variant name
 * (case-insensitive) or an index into the list. Null when it matches neither.
 *
 * Shared so the three callers can't drift: the UI resolves the URL param, the thumbnail capture
 * rig resolves an animation's endpoints in-page, and the batch runner pre-validates the same spec
 * against furniture.json before it launches a browser.
 * @param {Array<{name?: string}>|undefined} variants
 * @param {string|number} position
 * @returns {number|null}
 */
/**
 * The product code the backend prices a placed element as: a variant's own urlCode wins if it
 * declares one (clooods-co "Corner M L" orders as clooods-cx), else the item's.
 * @param {{furnitureData?: Object, variantIndex?: number}} element
 * @returns {string|undefined}
 */
export function activeProductCode(element) {
  return activeVariant(element)?.urlCode || element?.furnitureData?.urlCode;
}

/** The variant entry a placed element is in. */
export function activeVariant(element) {
  return element?.furnitureData?.properties?.variants?.[element?.variantIndex ?? 0];
}

const triple = (v) => [v?.x ?? 0, v?.y ?? 0, v?.z ?? 0];

/** A variant's rest pose as per-node overrides on bind ({position, rotation in degrees, visible}), as geometry.js applies it. */
export function variantRestPose(properties, variant) {
  const nodes = {};
  if (properties?.compositeGroupName) {
    nodes[properties.compositeGroupName] = variant.compositeVisible === false
      ? { visible: false }
      : { position: triple(variant.compositeOffset), rotation: [0, variant.compositeRotationY ?? 0, 0] };
  }
  for (const anim of variant.nodeAnimations ?? []) {
    if (!anim.node) continue;
    const entry = { ...nodes[anim.node] };
    if (anim.position) entry.position = triple(anim.position);
    if (anim.rotation) entry.rotation = triple(anim.rotation);
    if (anim.visible !== undefined) entry.visible = anim.visible !== false;
    nodes[anim.node] = entry;
  }
  return Object.fromEntries(Object.entries(nodes).sort(([a], [b]) => a.localeCompare(b)));
}

export function resolveVariantIndex(variants, position) {
  if (!variants?.length) return null;
  const raw = String(position).trim();
  const byName = variants.findIndex((v) => v?.name?.toLowerCase() === raw.toLowerCase());
  if (byName !== -1) return byName;
  const index = Number(raw);
  return Number.isInteger(index) && index >= 0 && index < variants.length ? index : null;
}
